import { onMounted, onUnmounted, ref, watch, type Ref } from 'vue';
import { SERP_HEADER_COLLAPSE_FROM, SERP_HEADER_EXPANDED_ZONE, SERP_HEADER_SCROLL_DELTA } from '../config/header';

function isHeaderCollapsed(collapsed: boolean, scrollTop: number, delta: number): boolean {
  if (scrollTop < SERP_HEADER_EXPANDED_ZONE) return false;
  if (delta > SERP_HEADER_SCROLL_DELTA && scrollTop > SERP_HEADER_COLLAPSE_FROM) return true;
  if (delta < -SERP_HEADER_SCROLL_DELTA) return false;

  return collapsed;
}

function isClampedToBottom(scrollTop: number): boolean {
  return scrollTop >= document.documentElement.scrollHeight - window.innerHeight - 1;
}

export function useHeaderCollapse(expanded: Ref<boolean>): Ref<boolean> {
  const collapsed = ref(false);
  let lastScrollTop = 0;

  const onScroll = (): void => {
    const scrollTop = Math.max(window.scrollY, 0);
    const delta = scrollTop - lastScrollTop;

    if (Math.abs(delta) <= SERP_HEADER_SCROLL_DELTA && scrollTop >= SERP_HEADER_EXPANDED_ZONE) return;

    if (delta < 0 && isClampedToBottom(scrollTop)) {
      lastScrollTop = scrollTop;
      return;
    }

    lastScrollTop = scrollTop;
    collapsed.value = !expanded.value && isHeaderCollapsed(collapsed.value, scrollTop, delta);
  };

  watch(expanded, (isExpanded) => {
    if (isExpanded) collapsed.value = false;
  });

  onMounted(() => {
    lastScrollTop = Math.max(window.scrollY, 0);
    window.addEventListener('scroll', onScroll, { passive: true });
  });

  onUnmounted(() => {
    window.removeEventListener('scroll', onScroll);
  });

  return collapsed;
}
